import { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useToast,
} from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { InputField } from '@/components/InputField';
import CenteredSpinner from '@/components/CenterSpinner/CenteredSpinner';
import { ModalFormData, modalFormSchema } from './schemas';
import ControlTeamFormSelect from '@/components/ControlTeamFormSelect';
import { StoreMainBanner } from '@/model';
import { ImageUpload } from '@/components/ImageUpload';
import { usePutStoreMainBannerMutation } from '@/services/queries/store-main-banner';

export interface StoreBannersEditModalProps {
  isOpen: boolean;
  item: StoreMainBanner;
  onClose: () => void;
  onDone?: () => void;
}

export const StoreBannersEditModal = ({ isOpen, item, onClose, onDone }: StoreBannersEditModalProps) => {
  const { t } = useTranslation();
  const toast = useToast();

  const putItemMutation = usePutStoreMainBannerMutation();

  const {
    handleSubmit,
    control,
    register,
    setValue,
    formState: { errors },
    clearErrors,
    reset,
  } = useForm<ModalFormData>({
    defaultValues: {
      team_uuid: item.team_uuid,
      source_url: item.source_url,
      title: item.title,
      image: undefined, // novo upload (opcional no edit)
    },
    resolver: zodResolver(modalFormSchema),
  });

  const formRef = useRef<HTMLFormElement>(null);

  const handleConfirm = useCallback(() => {
    formRef?.current?.requestSubmit();
  }, []);

  const handleClose = useCallback(() => {
    clearErrors();
    onClose?.();
    reset();
  }, [clearErrors, onClose, reset]);

  const onSubmit = useCallback(
    (data: ModalFormData) => {
      if (!item) return;

      const payload: any = {
        title: data.title,
        source_url: data.source_url,
        ...(data.team_uuid ? { team_uuid: data.team_uuid } : {}),
        ...(data.image ? { image: data.image } : {}), // só envia se trocar
      };

      putItemMutation.mutate(
        { id: item.uuid, payload },
        {
          onSuccess: () => {
            onDone?.();
            toast({
              status: 'success',
              title: t('pages.application_store.components.store_main_banners_edit_modal.edit_success'),
            });
          },
          onError: (resp: any) => {
            toast({
              status: 'error',
              title: (resp && resp.message) || t('pages.application_store.components.store_main_banners_edit_modal.edit_error'),
            });
          },
        },
      );
    },
    [item, onDone, putItemMutation, toast, t],
  );

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size='lg'>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Flex w='100%' justifyContent='space-between' alignItems='center' pb={2} borderBottom='2px solid' borderColor='black'>
            <Text fontSize='medium' fontWeight='bold'>
              {t('pages.application_store.components.store_main_banners_edit_modal.edit_title')}
            </Text>
          </Flex>
        </ModalHeader>

        <ModalBody>
          <Flex ref={formRef} as='form' onSubmit={handleSubmit(onSubmit)} direction='column' gap={6}>
            <ControlTeamFormSelect<ModalFormData, 'team_uuid'>
              control={control}
              name='team_uuid'
              prefetchOnOpen
              label={t('pages.application_store.components.store_main_banners_edit_modal.edit_team_label')}
              placeholder={t('pages.application_store.components.store_main_banners_edit_modal.edit_team_label')}
              showFlag
              width='100%'
              usePortal={false}
              error={errors?.team_uuid?.message as string | undefined}
            />

            <InputField
              withoutFloating
              label={t('pages.application_store.components.store_main_banners_edit_modal.edit_title_label')}
              {...register('title')}
              maxLength={100}
              rootProps={{
                isInvalid: !!errors?.title,
                errorMessage: errors?.title?.message,
              }}
            />

            <InputField
              withoutFloating
              label={t('pages.application_store.components.store_main_banners_edit_modal.edit_link_label')}
              {...register('source_url')}
              maxLength={255}
              rootProps={{
                isInvalid: !!errors?.source_url,
                errorMessage: errors?.source_url?.message,
              }}
            />

            <ImageUpload
              imageURL={item?.image_url || ''}
              onFileChange={file => setValue('image', file)}
              error={errors?.image?.message?.toString()}
            />
          </Flex>
        </ModalBody>

        <ModalFooter justifyContent='space-between' gap='kilo'>
          <Flex w='100%' justifyContent='end' gap={5}>
            <Button variant='outlined' color='gray.600' onClick={onClose}>
              {t('pages.application_store.components.store_main_banners_edit_modal.edit_cancel')}
            </Button>
            <Button onClick={handleConfirm}>
              {t('pages.application_store.components.store_main_banners_edit_modal.edit_save')}
            </Button>
          </Flex>
        </ModalFooter>
      </ModalContent>

      <CenteredSpinner isLoading={putItemMutation.isLoading} />
    </Modal>
  );
};
